import { bindAccessControlCatalog } from '@/chassis/core/auth/access-control/catalog';
import type { AllowResourceOptions } from '@/chassis/core/auth/access-control/catalog';
import type { PermissionOperationHint, PermissionResourceCatalog } from '@/chassis/core/auth/access-control/types';

/**
 * Frontend access-control catalog.
 *
 * Convention: use `resource:action` identifiers (ex.: `publictender-form:read`).
 * The backend must return the same identifiers in the logged user payload.
 */

export const permissions = {
    adminAreaRead: 'admin-area:read',
    adminAreaWrite: 'admin-area:write',
    publicTenderFormRead: 'publictender-form:read',
    publicTenderFormWrite: 'publictender-form:write',
    publicTenderListRead: 'publictender-list:read',
    publicTenderSearchRead: 'publictender-search:read',
    publicTenderCalendarRead: 'publictender-calendar:read',
    publicTenderCalendarWrite: 'publictender-calendar:write',
    publicTenderChatRead: 'publictender-chat:read',
    publicTenderChatWrite: 'publictender-chat:write',
    publicTenderBiddingRead: 'publictender-bidding:read',
    publicTenderBiddingWrite: 'publictender-bidding:write',
    publicTenderAuctioneerRead: 'publictender-auctioneer:read',
    publicTenderAuctioneerWrite: 'publictender-auctioneer:write',
    publicTenderApprovalRead: 'publictender-approval:read',
    publicTenderApprovalWrite: 'publictender-approval:write',
    publicTenderContestationRead: 'publictender-contestation:read',
    publicTenderContestationWrite: 'publictender-contestation:write',
    authAuthenticationRead: 'auth-authentication:read',
    authAuthenticationWrite: 'auth-authentication:write',
    authAuthorizationRead: 'auth-authorization:read',
    authAuthorizationWrite: 'auth-authorization:write',
    authTrackingRead: 'auth-tracking:read',
    authTrackingWrite: 'auth-tracking:write',
    registrationCitizenRead: 'registration-citizen:read',
    registrationCitizenWrite: 'registration-citizen:write',
    registrationCompanyRead: 'registration-company:read',
    registrationCompanyWrite: 'registration-company:write',
    registrationRepresentationRead: 'registration-representation:read',
    registrationRepresentationWrite: 'registration-representation:write',
    proposalsRead: 'proposals:read',
    bidsRead: 'bids:read',
    contractsRead: 'contracts:read',
    suppliersRead: 'suppliers:read',
    publicAgenciesRead: 'publicagencies:read',
    ticketsRead: 'tickets:read',
    helpCenterManage: 'help-center:manage',
    mediaManage: 'media:manage',
    notificationsManage: 'notifications:manage',
    documentsManage: 'documents:manage',
    generalParametersRead: 'general-parameters:read',
    generalParametersWrite: 'general-parameters:write',
    internationalizationRead: 'internationalization:read',
    internationalizationWrite: 'internationalization:write',
    subscriptionCatalogRead: 'subscription-catalog:read',
    subscriptionCatalogWrite: 'subscription-catalog:write',
    subscriptionAccountRead: 'subscription-account:read',
    subscriptionAccountWrite: 'subscription-account:write',
    subscriptionOrderRead: 'subscription-order:read',
    subscriptionOrderWrite: 'subscription-order:write',
    integrationSubscriptionRead: 'integration-subscription:read',
    integrationSubscriptionWrite: 'integration-subscription:write',
    integrationDigitalSignatureRead: 'integration-digitalsignature:read',
    integrationDigitalSignatureWrite: 'integration-digitalsignature:write',
    integrationPublicTenderRead: 'integration-publictender:read',
    integrationPublicTenderWrite: 'integration-publictender:write',
    integrationPartnersRead: 'integration-partners:read',
    integrationPartnersWrite: 'integration-partners:write',
    subscriptionPaymentRead: 'subscription-payment:read',
    subscriptionPaymentWrite: 'subscription-payment:write',
    aiAssistantSettingsRead: 'ai-assistant-settings:read',
    aiAssistantSettingsWrite: 'ai-assistant-settings:write',
    // 6.12 Directa24 — pagamentos (devem casar com o backend @RequirePermissions).
    paymentDepositCreate: 'payment-deposit:create',
    paymentDepositRead: 'payment-deposit:read',
    paymentSubscriptionCreate: 'payment-subscription:create',
    paymentSubscriptionRead: 'payment-subscription:read',
    paymentSubscriptionCancel: 'payment-subscription:cancel',
    agencyPricingRead: 'agency-pricing:read',
    agencyPricingWrite: 'agency-pricing:write',
    financialReportRead: 'financial-report:read',
    priceRecordsRead: 'price-records:read',
    priceRecordsWrite: 'price-records:write',
    agencyOnboardingRead: 'agency-onboarding:read',
} as const;

export type Permission = (typeof permissions)[keyof typeof permissions];

export const permissionResources = {
    adminArea: { read: permissions.adminAreaRead, write: permissions.adminAreaWrite },
    publicTenderForm: { read: permissions.publicTenderFormRead, write: permissions.publicTenderFormWrite },
    publicTenderList: { read: permissions.publicTenderListRead, write: permissions.publicTenderFormWrite },
    publicTenderSearch: { read: permissions.publicTenderSearchRead },
    publicTenderCalendar: { read: permissions.publicTenderCalendarRead, write: permissions.publicTenderCalendarWrite },
    publicTenderChat: { read: permissions.publicTenderChatRead, write: permissions.publicTenderChatWrite },
    publicTenderBidding: { read: permissions.publicTenderBiddingRead, write: permissions.publicTenderBiddingWrite },
    publicTenderAuctioneer: { read: permissions.publicTenderAuctioneerRead, write: permissions.publicTenderAuctioneerWrite },
    publicTenderApproval: { read: permissions.publicTenderApprovalRead, write: permissions.publicTenderApprovalWrite },
    publicTenderContestation: { read: permissions.publicTenderContestationRead, write: permissions.publicTenderContestationWrite },
    userPages: { read: permissions.publicTenderListRead },
    authAuthentication: { read: permissions.authAuthenticationRead, write: permissions.authAuthenticationWrite },
    authAuthorization: { read: permissions.authAuthorizationRead, write: permissions.authAuthorizationWrite },
    authTracking: { read: permissions.authTrackingRead, write: permissions.authTrackingWrite },
    registrationCitizen: { read: permissions.registrationCitizenRead, write: permissions.registrationCitizenWrite },
    registrationCompany: { read: permissions.registrationCompanyRead, write: permissions.registrationCompanyWrite },
    registrationRepresentation: { read: permissions.registrationRepresentationRead, write: permissions.registrationRepresentationWrite },
    proposals: { read: permissions.proposalsRead },
    bids: { read: permissions.bidsRead },
    contracts: { read: permissions.contractsRead },
    suppliers: { read: permissions.suppliersRead },
    publicAgencies: { read: permissions.publicAgenciesRead },
    tickets: {
        read: permissions.ticketsRead,
        write: permissions.ticketsRead, // temporary: reuse read permission until backend exposes tickets:write
    },
    helpCenter: { manage: permissions.helpCenterManage },
    media: { manage: permissions.mediaManage },
    notifications: { manage: permissions.notificationsManage },
    documents: { manage: permissions.documentsManage },
    generalParameters: { read: permissions.generalParametersRead, write: permissions.generalParametersWrite },
    internationalization: { read: permissions.internationalizationRead, write: permissions.internationalizationWrite },
    subscriptionCatalog: { read: permissions.subscriptionCatalogRead, write: permissions.subscriptionCatalogWrite },
    subscriptionAccount: { read: permissions.subscriptionAccountRead, write: permissions.subscriptionAccountWrite },
    subscriptionOrder: { read: permissions.subscriptionOrderRead, write: permissions.subscriptionOrderWrite },
    integrationSubscription: { read: permissions.integrationSubscriptionRead, write: permissions.integrationSubscriptionWrite },
    integrationDigitalSignature: { read: permissions.integrationDigitalSignatureRead, write: permissions.integrationDigitalSignatureWrite },
    integrationPublicTender: { read: permissions.integrationPublicTenderRead, write: permissions.integrationPublicTenderWrite },
    integrationPartners: { read: permissions.integrationPartnersRead, write: permissions.integrationPartnersWrite },
    subscriptionPayment: { read: permissions.subscriptionPaymentRead, write: permissions.subscriptionPaymentWrite },
    aiAssistantSettings: { read: permissions.aiAssistantSettingsRead, write: permissions.aiAssistantSettingsWrite },
    paymentDeposit: { read: permissions.paymentDepositRead, write: permissions.paymentDepositCreate },
    paymentSubscription: { read: permissions.paymentSubscriptionRead, write: permissions.paymentSubscriptionCreate },
    agencyPricing: { read: permissions.agencyPricingRead, write: permissions.agencyPricingWrite },
    financialReport: { read: permissions.financialReportRead },
    priceRecords: { read: permissions.priceRecordsRead, write: permissions.priceRecordsWrite },
    agencyOnboarding: { read: permissions.agencyOnboardingRead },
} as const satisfies PermissionResourceCatalog<Permission>;

export type PermissionResourceKey = keyof typeof permissionResources;
export type { PermissionOperationHint, PermissionOperation } from '@/chassis/core/auth/access-control/types';
export type { AllowResourceOptions } from '@/chassis/core/auth/access-control/catalog';

const accessControlBindings = bindAccessControlCatalog<typeof permissionResources, typeof permissions, Permission>(permissions, permissionResources);

export const resolveResourcePermission = accessControlBindings.resolveResourcePermission;
export const resolveRoutePermission = accessControlBindings.resolveRoutePermission;
export const allowResource = accessControlBindings.allowResource;
export const inferOperationFromPath = accessControlBindings.inferOperationFromPath;

export type SideMenuEntry = SideMenuDivider | SideMenuItem;
export type SideMenuDivider = string;

/** Publico do menu do portal, derivado da representacao selecionada no topo (layout.tsx). */
export type SideMenuAudience = 'admin' | 'citizen' | 'supplier' | 'publicAgency';

export interface SideMenuItem {
    icon: string;
    title?: string;
    i18nTitle?: string;
    badge?: number;
    pathname?: string;
    /** Endereco aberto em nova aba (arquivo ou site), em vez de uma rota do portal. */
    externalUrl?: string;
    subMenu?: SideMenuEntry[];
    /** Optional permission requirement (single or multiple). */
    allow?: Permission | Permission[];
    /** Hide this item when the current authenticated session is guest. */
    hideForGuest?: boolean;
    /** Publicos que veem o item. Ausente = todos. Aplicado depois do recorte do menu por publico. */
    audiences?: SideMenuAudience[];
    /** Items explicitly listed here should not be auto-hidden by section logic, even if nested. */
    ignore?: boolean;
}

export const GENERAL_MENU_DIVIDER = 'VISÃO GERAL';

/*
'CADASTROS GERAIS',
{
    icon: 'Settings',
    i18nTitle: 'menu.parameters',
    allow: permissions.generalParametersRead,
    subMenu: [
        { icon: 'Dot', pathname: '/systemparameters', i18nTitle: 'menu.systemParameters', allow: permissions.generalParametersRead },
        { icon: 'Dot', pathname: '/preferenceparameters', i18nTitle: 'menu.preferenceParameters', allow: permissions.generalParametersRead },
        { icon: 'Dot', pathname: '/systemresources', i18nTitle: 'menu.systemResources', allow: permissions.generalParametersRead },
    ],
},
{
    icon: 'Globe',
    i18nTitle: 'menu.internationalization',
    allow: permissions.internationalizationRead,
    subMenu: [
        { icon: 'Dot', pathname: '/countries', i18nTitle: 'menu.countries', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/currencies', i18nTitle: 'menu.currencies', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/locations', i18nTitle: 'menu.locations', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/timezones', i18nTitle: 'menu.timezones', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/languagemanagement', i18nTitle: 'menu.languages', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/translationkeys', i18nTitle: 'menu.translationKeys', allow: permissions.internationalizationRead },
        { icon: 'Dot', pathname: '/zipcodes', i18nTitle: 'menu.zipcodes', allow: permissions.internationalizationRead },
    ],
},
{
    icon: 'Bell',
    i18nTitle: 'menu.communication',
    allow: permissions.notificationsManage,
    subMenu: [
        { icon: 'Dot', pathname: '/notifications', i18nTitle: 'menu.notifications', allow: permissions.notificationsManage },
        { icon: 'Dot', pathname: '/notificationtemplates', i18nTitle: 'menu.notificationTemplates', allow: permissions.notificationsManage },
        { icon: 'Dot', pathname: '/notificationcategories', i18nTitle: 'menu.notificationCategories', allow: permissions.notificationsManage },
        { icon: 'Dot', pathname: '/communications', i18nTitle: 'menu.communicationsCenter', allow: permissions.notificationsManage },
        { icon: 'Dot', pathname: '/communicationcategories', i18nTitle: 'menu.communicationCategories', allow: permissions.notificationsManage },
    ],
},
{
    icon: 'FileText',
    i18nTitle: 'menu.documents',
    allow: permissions.documentsManage,
    subMenu: [
        { icon: 'Dot', pathname: '/documenttemplates', i18nTitle: 'menu.documentTemplates', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/generateddocuments', i18nTitle: 'menu.generatedDocuments', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/documentcategories', i18nTitle: 'menu.documentCategories', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/documentgroups', i18nTitle: 'menu.documentGroups', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/documentsignatures', i18nTitle: 'menu.documentSignatures', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/uploadeddocuments', i18nTitle: 'menu.uploadedDocuments', allow: permissions.documentsManage },
        { icon: 'Dot', pathname: '/uploadedimages', i18nTitle: 'menu.uploadedImages', allow: permissions.documentsManage },
    ],
},
{
    icon: 'Image',
    i18nTitle: 'menu.media',
    allow: permissions.mediaManage,
    subMenu: [
        { icon: 'Dot', pathname: '/mediagroups', i18nTitle: 'menu.mediaGroups', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/mediacategories', i18nTitle: 'menu.mediaCategories', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/images', i18nTitle: 'menu.images', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/videos', i18nTitle: 'menu.videos', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/streamvideo', i18nTitle: 'menu.streamVideo', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/streamaudio', i18nTitle: 'menu.streamAudio', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/audios', i18nTitle: 'menu.audios', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/subtitles', i18nTitle: 'menu.subtitles', allow: permissions.mediaManage },
        { icon: 'Dot', pathname: '/transcriptions', i18nTitle: 'menu.transcriptions', allow: permissions.mediaManage },
    ],
},
'CADASTROS DE AUTENTICAÇÃO',
{
    icon: 'Shield',
    i18nTitle: 'menu.authentication',
    allow: permissions.authAuthenticationRead,
    subMenu: [
        { icon: 'Dot', pathname: '/usercredentials', i18nTitle: 'menu.userCredentials', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/userlogins', i18nTitle: 'menu.userLogins', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/confirmationcodes', i18nTitle: 'menu.confirmationCodes', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/codecategories', i18nTitle: 'menu.codeCategories', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/devicecategories', i18nTitle: 'menu.deviceCategories', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/devices', i18nTitle: 'menu.devices', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/multifactorcategories', i18nTitle: 'menu.mfaCategories', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/multifactorcredentials', i18nTitle: 'menu.mfaCredentials', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/ipblocks', i18nTitle: 'menu.ipBlocks', allow: permissions.authAuthenticationRead },
        { icon: 'Dot', pathname: '/termsacceptances', i18nTitle: 'menu.termsAcceptances', allow: permissions.authAuthenticationRead },
    ],
},
{
    icon: 'KeyRound',
    i18nTitle: 'menu.authorization',
    allow: permissions.authAuthorizationRead,
    subMenu: [
        { icon: 'Dot', pathname: '/accessgroups', i18nTitle: 'menu.accessGroups', allow: permissions.authAuthorizationRead },
        { icon: 'Dot', pathname: '/roleassignments', i18nTitle: 'menu.roleAssignments', allow: permissions.authAuthorizationRead },
        { icon: 'Dot', pathname: '/rolespermissions', i18nTitle: 'menu.rolesPermissions', allow: permissions.authAuthorizationRead },
        { icon: 'Dot', pathname: '/useraccounts', i18nTitle: 'menu.userAccounts', allow: permissions.authAuthorizationRead },
        { icon: 'Dot', pathname: '/userterms', i18nTitle: 'menu.userTerms', allow: permissions.authAuthorizationRead },
    ],
},
{
    icon: 'Activity',
    i18nTitle: 'menu.tracking',
    allow: permissions.authTrackingRead,
    subMenu: [
        { icon: 'Dot', pathname: '/occurrencecategories', i18nTitle: 'menu.occurrenceCategories', allow: permissions.authTrackingRead },
        { icon: 'Dot', pathname: '/occurrences', i18nTitle: 'menu.occurrences', allow: permissions.authTrackingRead },
        { icon: 'Dot', pathname: '/useractivity', i18nTitle: 'menu.userActivity', allow: permissions.authTrackingRead },
    ],
},
{
        icon: 'BookOpen',
        i18nTitle: 'menu.helpCenter',
        allow: permissions.helpCenterManage,
        subMenu: [
            { icon: 'Dot', pathname: '/aboutpages', i18nTitle: 'menu.aboutPages', allow: permissions.helpCenterManage },
            { icon: 'Dot', pathname: '/helpcontacts', i18nTitle: 'menu.helpContacts', allow: permissions.helpCenterManage },
            { icon: 'Dot', pathname: '/userreviews', i18nTitle: 'menu.userReviews', allow: permissions.helpCenterManage },
        ],
    },
*/

export const sideMenuConfig: SideMenuEntry[] = [
    GENERAL_MENU_DIVIDER,
    { icon: 'BarChart3', pathname: '/mydashboard', i18nTitle: 'menu.mydashboard', allow: permissions.publicTenderListRead, hideForGuest: true },
    { icon: 'Search', pathname: '/publictenders/search', i18nTitle: 'menu.publicTendersSearch', allow: permissions.publicTenderSearchRead },
    'MINHA CONTA',
    // Meu perfil, Minhas representações e Meu plano (29/10). "Meu plano" só para quem
    // representa legalmente o fornecedor: o layout confere o tipo de representação.
    { icon: 'UserRound', pathname: '/myprofile', title: 'Meu perfil', hideForGuest: true },
    { icon: 'Users', pathname: '/myrepresentations', i18nTitle: 'menu.myRepresentations', allow: permissions.registrationRepresentationRead, hideForGuest: true },
    // Planos (29/10): plano e pagamentos agrupados numa página com abas (/meu-plano). O
    // Histórico de pagamentos abre a aba Pagamentos. "Meu plano" só aparece para quem
    // representa legalmente o fornecedor (filtro hiddenPlan do layout, que olha só o pathname
    // exato '/meu-plano'); o Histórico aparece para qualquer fornecedor, como antes.
    // Fica em MINHA CONTA, e nao em PARA FORNECEDORES: o layout corta o menu do portal na
    // primeira secao que nao seja VISAO GERAL/MINHA CONTA/AJUDA (PoC 052/2026, taxa por sucesso).
    {
        icon: 'Crown',
        title: 'Planos',
        allow: permissions.proposalsRead,
        hideForGuest: true,
        audiences: ['supplier'],
        subMenu: [
            { icon: 'BadgeCheck', pathname: '/meu-plano', title: 'Meu plano', allow: permissions.subscriptionOrderRead, hideForGuest: true },
            { icon: 'Receipt', pathname: '/meu-plano?aba=pagamentos', title: 'Histórico de pagamentos', allow: permissions.proposalsRead, hideForGuest: true },
        ],
    },
    // O administrador global continua com a página avulsa de pagamentos.
    { icon: 'Receipt', pathname: '/historico-pagamentos', title: 'Histórico de Pagamentos', allow: permissions.proposalsRead, hideForGuest: true, audiences: ['admin'] },
    // { icon: 'UserPlus', pathname: '/representationrequests', i18nTitle: 'menu.representationRequests', allow: permissions.registrationRepresentationRead },
    // Ajuda movida para o rodapé (última seção do menu cidadão/órgão).
    'AJUDA',
    // Manual do Fornecedor em HTML (/manual-do-fornecedor), aberto no meio da tela com o botao
    // Baixar PDF (public/manuais). Para o cidadao, que ainda vai cadastrar a empresa, e para o
    // fornecedor; o orgao nao precisa dele.
    { icon: 'BookOpenText', pathname: '/manual-do-fornecedor', title: 'Manual do Fornecedor', hideForGuest: true, audiences: ['citizen', 'supplier', 'admin'] },
    // Central de Ajuda no menu, logo acima de Meus chamados (29/10): saiu dos botões do
    // manual e de Meus chamados, que repetiam o mesmo atalho.
    { icon: 'BadgeHelp', pathname: '/myfaqs', title: 'Central de Ajuda', allow: permissions.publicTenderListRead },
    { icon: 'MessagesSquare', pathname: '/mytickets', i18nTitle: 'menu.myTickets', allow: permissions.publicTenderListRead },
    'PARA FORNECEDORES',
    { icon: 'TrendingUp', pathname: '/proposals', i18nTitle: 'menu.proposalsRegister', allow: permissions.proposalsRead },
    'ADMINISTRAÇÃO',
    { icon: 'LayoutDashboard', pathname: '/admin/dashboard', title: 'Painel Administrativo', allow: permissions.adminAreaRead },
    { icon: 'DollarSign', pathname: '/precos-participacao', title: 'Preços de Participação', allow: permissions.agencyPricingRead },
    { icon: 'BadgeCheck', pathname: '/liberacao-manual', title: 'Liberação Manual', allow: permissions.adminAreaRead },
    { icon: 'ClipboardList', pathname: '/atas-srp', title: 'Atas de Registro de Preços', allow: permissions.priceRecordsRead },
    { icon: 'UserPlus', pathname: '/onboarding-orgao', title: 'Onboarding de Órgão', allow: permissions.agencyOnboardingRead },
    { icon: 'Rocket', pathname: '/testes-gratis', title: 'Testes grátis', allow: permissions.adminAreaRead },
    { icon: 'Presentation', pathname: '/pedidos-demonstracao', title: 'Pedidos de demonstração', allow: permissions.adminAreaRead },
    { icon: 'BarChart3', pathname: '/relatorios-financeiros', title: 'Relatórios Financeiros', allow: permissions.financialReportRead },
    { icon: 'Receipt', pathname: '/cobrancas-por-sucesso', title: 'Cobranças por Sucesso', allow: permissions.financialReportRead },
    { icon: 'LifeBuoy', pathname: '/admin/support', title: 'Visão Suporte', allow: permissions.adminAreaRead },
    { icon: 'Celeri', pathname: '/ai-assistant-settings', title: 'Assistente IA (Beta)', allow: permissions.aiAssistantSettingsWrite }, // símbolo da Celeri (layout.tsx, IconeMenu)
    { icon: 'FileCheck', pathname: '/representationapprovals', i18nTitle: 'menu.representationApprovals', allow: permissions.registrationRepresentationRead },
    { icon: 'Gavel', pathname: '/publictenders/search', i18nTitle: 'menu.publicTenders', allow: permissions.publicTenderListRead },
    {
        icon: 'HelpCircle',
        i18nTitle: 'menu.ticketsService',
        allow: permissions.ticketsRead,
        subMenu: [
            { icon: 'Dot', pathname: '/faqs', i18nTitle: 'menu.helpFaqs', allow: permissions.helpCenterManage },
            { icon: 'Dot', pathname: '/tickets', i18nTitle: 'menu.tickets', allow: permissions.ticketsRead },
            { icon: 'Dot', pathname: '/ticketsentries', i18nTitle: 'menu.ticketEntries', allow: permissions.ticketsRead },
            { icon: 'Dot', pathname: '/ticketscategories', i18nTitle: 'menu.ticketCategories', allow: permissions.ticketsRead },
            { icon: 'Dot', pathname: '/ticketspriorities', i18nTitle: 'menu.ticketPriorities', allow: permissions.ticketsRead },
            { icon: 'Dot', pathname: '/ticketscriticalities', i18nTitle: 'menu.ticketCriticalities', allow: permissions.ticketsRead },
            { icon: 'Dot', pathname: '/tickethandlingteams', i18nTitle: 'menu.ticketHandlingTeams', allow: permissions.ticketsRead },
        ],
    },
    'CADASTROS DE CIDADÃOS E EMPRESAS',
    {
        icon: 'Users',
        i18nTitle: 'menu.citizenRegistration',
        allow: permissions.registrationCitizenRead,
        subMenu: [
            { icon: 'Dot', pathname: '/citizens', i18nTitle: 'menu.citizens', allow: permissions.registrationCitizenRead },
            { icon: 'Dot', pathname: '/citizenpreferences', i18nTitle: 'menu.citizenPreferences', allow: permissions.registrationCitizenRead },
            { icon: 'Dot', pathname: '/publicagencyfollows', i18nTitle: 'menu.publicAgencyFollows', allow: permissions.registrationCitizenRead },
        ],
    },
    {
        icon: 'Building2',
        i18nTitle: 'menu.companyRegistration',
        allow: permissions.registrationCompanyRead,
        subMenu: [
            { icon: 'Dot', pathname: '/suppliers', i18nTitle: 'menu.suppliers', allow: permissions.registrationCompanyRead },
            { icon: 'Dot', pathname: '/publicagencies', i18nTitle: 'menu.publicAgencies', allow: permissions.adminAreaRead },
            { icon: 'Dot', pathname: '/publicagencypreferences', i18nTitle: 'menu.publicAgencyPreferences', allow: permissions.registrationCompanyRead },
            { icon: 'Dot', pathname: '/supplierpreferences', i18nTitle: 'menu.supplierPreferences', allow: permissions.registrationCompanyRead },
            { icon: 'Dot', pathname: '/publicsectors', i18nTitle: 'menu.publicSectors', allow: permissions.registrationCompanyRead },
            { icon: 'Dot', pathname: '/supplycategories', i18nTitle: 'menu.supplyCategories', allow: permissions.registrationCompanyRead },
        ],
    },
    {
        icon: 'Handshake',
        i18nTitle: 'menu.representationRegistration',
        allow: permissions.registrationRepresentationRead,
        subMenu: [
            { icon: 'Dot', pathname: '/representations', i18nTitle: 'menu.representations', allow: permissions.registrationRepresentationRead },
            { icon: 'Dot', pathname: '/representativeprofiles', i18nTitle: 'menu.representativeProfiles', allow: permissions.registrationRepresentationRead },
            { icon: 'Dot', pathname: '/representationrequests/admin', i18nTitle: 'menu.representationRequestsAdmin', allow: permissions.registrationRepresentationRead },
            { icon: 'Dot', pathname: '/representationcategories', i18nTitle: 'menu.representationCategories', allow: permissions.registrationRepresentationRead },
            { icon: 'Dot', pathname: '/representationrequests/admin', i18nTitle: 'menu.representationLinkRequests', allow: permissions.registrationRepresentationRead },
            { icon: 'Dot', pathname: '/representationapprovals', i18nTitle: 'menu.representationApprovalsAdmin', allow: permissions.registrationRepresentationRead },
        ],
    },
    'CADASTROS DE LICITAÇÕES',
    {
        icon: 'Hammer',
        i18nTitle: 'menu.planning',
        allow: permissions.publicTenderListRead,
        subMenu: [
            { icon: 'Dot', pathname: '/publictenders', i18nTitle: 'menu.tenders', allow: permissions.publicTenderListRead },
            { icon: 'Dot', pathname: '/items', i18nTitle: 'menu.items', allow: permissions.publicTenderListRead },
            { icon: 'Dot', pathname: '/lots', i18nTitle: 'menu.lots', allow: permissions.publicTenderListRead },
            { icon: 'Dot', pathname: '/publictendercategories', i18nTitle: 'menu.tenderCategories', allow: permissions.publicTenderListRead },
            { icon: 'Dot', pathname: '/itemcategories', i18nTitle: 'menu.itemCategories', allow: permissions.publicTenderListRead },
            { icon: 'Dot', pathname: '/lotcategories', i18nTitle: 'menu.lotCategories', allow: permissions.publicTenderListRead },
        ],
    },
    /*{
        icon: 'Calendar',
        i18nTitle: 'menu.calendar',
        allow: permissions.publicTenderCalendarRead,
        subMenu: [
            { icon: 'Dot', pathname: '/calendar', i18nTitle: 'menu.calendarPage', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/calendarbalances', i18nTitle: 'menu.calendarBalances', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/calendarmarks', i18nTitle: 'menu.calendarMarks', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/calendarmarkingcategories', i18nTitle: 'menu.calendarMarkingCategories', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/holidays', i18nTitle: 'menu.holidays', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/schedulingcategories', i18nTitle: 'menu.schedulingCategories', allow: permissions.publicTenderCalendarRead },
            { icon: 'Dot', pathname: '/schedulings', i18nTitle: 'menu.schedulings', allow: permissions.publicTenderCalendarRead },
        ],
    },
    {
        icon: 'MessageSquare',
        i18nTitle: 'menu.communications',
        allow: permissions.publicTenderChatRead,
        subMenu: [
            { icon: 'Dot', pathname: '/dialogcategories', i18nTitle: 'menu.dialogCategories', allow: permissions.publicTenderChatRead },
            { icon: 'Dot', pathname: '/dialogparticipants', i18nTitle: 'menu.dialogParticipants', allow: permissions.publicTenderChatRead },
            { icon: 'Dot', pathname: '/dialogs', i18nTitle: 'menu.dialogs', allow: permissions.publicTenderChatRead },
            { icon: 'Dot', pathname: '/messages', i18nTitle: 'menu.messages', allow: permissions.publicTenderChatRead },
        ],
    },*/
    {
        icon: 'Target',
        i18nTitle: 'menu.sessionsBids',
        allow: permissions.publicTenderBiddingRead,
        subMenu: [
            { icon: 'Dot', pathname: '/lotauctionsessions', i18nTitle: 'menu.auctionSessions', allow: permissions.publicTenderBiddingRead },
            { icon: 'Dot', pathname: '/bids', i18nTitle: 'menu.bids', allow: permissions.publicTenderBiddingRead },
            { icon: 'Dot', pathname: '/proposals', i18nTitle: 'menu.proposals', allow: permissions.publicTenderBiddingRead },
            { icon: 'Dot', pathname: '/biddingprocedurecategories', i18nTitle: 'menu.biddingProcedureCategories', allow: permissions.publicTenderBiddingRead },
        ],
    },
    {
        icon: 'ClipboardCheck',
        i18nTitle: 'menu.approval',
        allow: permissions.publicTenderApprovalRead,
        subMenu: [
            { icon: 'Dot', pathname: '/contracts', i18nTitle: 'menu.contracts', allow: permissions.publicTenderApprovalRead },
            { icon: 'Dot', pathname: '/approvals', i18nTitle: 'menu.approvals', allow: permissions.publicTenderApprovalRead },
            { icon: 'Dot', pathname: '/awards', i18nTitle: 'menu.awards', allow: permissions.publicTenderApprovalRead },
            { icon: 'Dot', pathname: '/selectioncriteriacategories', i18nTitle: 'menu.selectionCriteriaCategories', allow: permissions.publicTenderApprovalRead },
        ],
    },
    {
        icon: 'AlertTriangle',
        i18nTitle: 'menu.contestation',
        allow: permissions.publicTenderContestationRead,
        subMenu: [
            { icon: 'Dot', pathname: '/appeals', i18nTitle: 'menu.appeals', allow: permissions.publicTenderContestationRead },
            { icon: 'Dot', pathname: '/appealresponses', i18nTitle: 'menu.appealResponses', allow: permissions.publicTenderContestationRead },
            { icon: 'Dot', pathname: '/appealcategories', i18nTitle: 'menu.appealCategories', allow: permissions.publicTenderContestationRead },
            { icon: 'Dot', pathname: '/evaluationcriteria', i18nTitle: 'menu.evaluationCriteria', allow: permissions.publicTenderContestationRead },
        ],
    },
    'CADASTROS DE ASSINATURAS',
    {
        icon: 'Layers',
        i18nTitle: 'menu.catalog',
        allow: permissions.subscriptionCatalogRead,
        subMenu: [
            { icon: 'Dot', pathname: '/services', i18nTitle: 'menu.services', allow: permissions.subscriptionCatalogRead },
            { icon: 'Dot', pathname: '/categories', i18nTitle: 'menu.serviceCategories', allow: permissions.subscriptionCatalogRead },
            { icon: 'Dot', pathname: '/offers', i18nTitle: 'menu.serviceOffers', allow: permissions.subscriptionCatalogRead },
        ],
    },
    {
        icon: 'Wallet',
        i18nTitle: 'menu.account',
        allow: permissions.subscriptionAccountRead,
        subMenu: [
            { icon: 'Dot', pathname: '/paymentmethods', i18nTitle: 'menu.paymentMethods', allow: permissions.subscriptionAccountRead },
            { icon: 'Dot', pathname: '/paymentmethodcategories', i18nTitle: 'menu.paymentMethodCategories', allow: permissions.subscriptionAccountRead },
            { icon: 'Dot', pathname: '/financialaccounts', i18nTitle: 'menu.financialAccounts', allow: permissions.subscriptionAccountRead },
            { icon: 'Dot', pathname: '/financialbalances', i18nTitle: 'menu.financialBalances', allow: permissions.subscriptionAccountRead },
        ],
    },
    {
        icon: 'ShoppingCart',
        i18nTitle: 'menu.order',
        allow: permissions.subscriptionOrderRead,
        subMenu: [
            { icon: 'Dot', pathname: '/subscriptionplans', i18nTitle: 'menu.subscriptionPlans', allow: permissions.subscriptionOrderRead },
            { icon: 'Dot', pathname: '/subscriptions', i18nTitle: 'menu.subscriptions', allow: permissions.subscriptionOrderRead },
            { icon: 'Dot', pathname: '/origins', i18nTitle: 'menu.orderOrigins', allow: permissions.subscriptionOrderRead },
            { icon: 'Dot', pathname: '/fees', i18nTitle: 'menu.commercialFees', allow: permissions.subscriptionOrderRead },
            { icon: 'Dot', pathname: '/cancellations', i18nTitle: 'menu.cancellations', allow: permissions.subscriptionOrderRead },
            { icon: 'Dot', pathname: '/cancellationmotives', i18nTitle: 'menu.cancellationMotives', allow: permissions.subscriptionOrderRead },
        ],
    },
    {
        icon: 'CreditCard',
        i18nTitle: 'menu.payment',
        allow: permissions.subscriptionPaymentRead,
        subMenu: [
            { icon: 'Dot', pathname: '/payments', i18nTitle: 'menu.payments', allow: permissions.subscriptionPaymentRead },
            { icon: 'Dot', pathname: '/invoices', i18nTitle: 'menu.invoices', allow: permissions.subscriptionPaymentRead },
            { icon: 'Dot', pathname: '/paymentcaptures', i18nTitle: 'menu.paymentCaptures', allow: permissions.subscriptionPaymentRead },
            { icon: 'Dot', pathname: '/paymentcategories', i18nTitle: 'menu.paymentCategories', allow: permissions.subscriptionPaymentRead },
            { icon: 'Dot', pathname: '/refunds', i18nTitle: 'menu.refunds', allow: permissions.subscriptionPaymentRead },
            { icon: 'Dot', pathname: '/refundreasons', i18nTitle: 'menu.refundReasons', allow: permissions.subscriptionPaymentRead },
        ],
    },
    'CADASTROS DE INTEGRAÇÕES',
    {
        icon: 'Plug',
        i18nTitle: 'menu.pncp',
        allow: permissions.integrationPublicTenderRead,
        subMenu: [
            { icon: 'Dot', pathname: '/pncpcredentials', i18nTitle: 'menu.pncpCredentials', allow: permissions.integrationPublicTenderRead },
            { icon: 'Dot', pathname: '/pncppublications', i18nTitle: 'menu.pncpPublications', allow: permissions.integrationPublicTenderRead },
            { icon: 'Dot', pathname: '/pncpmappings', i18nTitle: 'menu.pncpMappings', allow: permissions.integrationPublicTenderRead },
        ],
    },
    {
        icon: 'Cloud',
        i18nTitle: 'menu.subscriptionIntegrations',
        allow: permissions.integrationSubscriptionRead,
        subMenu: [
            { icon: 'Dot', pathname: '/gatewayintegration', i18nTitle: 'menu.gatewayIntegration', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/gatewaycustomers', i18nTitle: 'menu.gatewayCustomers', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/gatewaycredentials', i18nTitle: 'menu.gatewayCredentials', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/gatewayjobs', i18nTitle: 'menu.gatewayJobs', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/gatewayattempt-logs', i18nTitle: 'menu.gatewayAttemptLogs', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/gatewaywebhooks', i18nTitle: 'menu.gatewayWebhooks', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/cancellationsintegrations', i18nTitle: 'menu.integrationCancellations', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/refundsintegrations', i18nTitle: 'menu.integrationRefunds', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/subscriptionsintegrations', i18nTitle: 'menu.integrationSubscriptions', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/cancellationmotivesintegrations', i18nTitle: 'menu.integrationCancellationMotives', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/refundmotivesintegrations', i18nTitle: 'menu.integrationRefundMotives', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/subscriptionplansintegrations', i18nTitle: 'menu.integrationSubscriptionPlans', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/paymentsgateway', i18nTitle: 'menu.gatewayPayments', allow: permissions.integrationSubscriptionRead },
            { icon: 'Dot', pathname: '/paymentmethodsgateway', i18nTitle: 'menu.gatewayPaymentMethods', allow: permissions.integrationSubscriptionRead },
        ],
    },
    {
        icon: 'PenTool',
        i18nTitle: 'menu.digitalSignature',
        allow: permissions.integrationDigitalSignatureRead,
        subMenu: [
            { icon: 'Dot', pathname: '/signaturegateway', i18nTitle: 'menu.signatureGateway', allow: permissions.integrationDigitalSignatureRead },
            { icon: 'Dot', pathname: '/signatureentries', i18nTitle: 'menu.signatureEntries', allow: permissions.integrationDigitalSignatureRead },
            { icon: 'Dot', pathname: '/attemptlogs', i18nTitle: 'menu.attemptLogs', allow: permissions.integrationDigitalSignatureRead },
        ],
    },
    {
        icon: 'Plug',
        i18nTitle: 'menu.transfereGov',
        allow: permissions.integrationPublicTenderRead,
        subMenu: [
            { icon: 'Dot', pathname: '/transferegovcredentials', i18nTitle: 'menu.transfereGovCredentials', allow: permissions.integrationPublicTenderRead },
            { icon: 'Dot', pathname: '/transferegovprocesslogs', i18nTitle: 'menu.transfereGovLogs', allow: permissions.integrationPublicTenderRead },
        ],
    },
    {
        icon: 'Key',
        i18nTitle: 'menu.integrationPartners',
        allow: permissions.integrationPartnersRead,
        subMenu: [
            { icon: 'Dot', pathname: '/partners', i18nTitle: 'menu.partners', allow: permissions.integrationPartnersRead },
        ],
    },
];

export const guestSideMenuConfig: SideMenuEntry[] = [
    GENERAL_MENU_DIVIDER,
    { icon: 'BarChart3', pathname: '/mydashboard', i18nTitle: 'menu.mydashboard', allow: permissions.publicTenderListRead },
    { icon: 'Search', pathname: '/publictenders/search', i18nTitle: 'menu.publicTendersSearch', allow: permissions.publicTenderSearchRead },
    { icon: 'HelpCircle', pathname: '/myfaqs', i18nTitle: 'menu.faqs', allow: permissions.publicTenderListRead },
];
